import { useMemo, useState } from "react";
import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import addressApi from "api/landlord/address";
import {
  BrandingTarget,
  BrandingTargetsResponse,
  Property
} from "types/landlord/address";
import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  InputSize,
  Modal
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import {
  isSettledStatus,
  useBulkBrandingProgress
} from "providers/BulkBrandingProgress";
import { BrandingTargetsList } from "./BrandingTargetsList";
import { BrandingToApplyPreview } from "./BrandingToApplyPreview";
import { ConfirmApplyBrandingDialog } from "./ConfirmApplyBrandingDialog";
import {
  BRANDING_TARGETS_PER_PAGE,
  getApplyErrorMessage,
  MAX_BRANDING_TARGETS,
  overriddenPmsFields,
  propertiesLabel,
  sourceLandlordFields
} from "./helpers";

const SEARCH_DEBOUNCE_MS = 500;

export const ApplyBrandingModal = ({
  opened,
  onClose,
  sourceProperty,
  showLandlordFields
}: {
  opened: boolean;
  onClose: () => void;
  sourceProperty: Property;
  showLandlordFields: boolean;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const {
    startTracking,
    run: activeRun,
    status: activeRunStatus
  } = useBulkBrandingProgress();
  const hasActiveRun =
    activeRun !== null &&
    !(activeRunStatus && isSettledStatus(activeRunStatus.status));
  const [selected, setSelected] = useState<Map<number, BrandingTarget>>(
    () => new Map()
  );
  const [applyMode, setApplyMode] = useState<"explicit" | "all">("explicit");
  const [excludedIds, setExcludedIds] = useState<Set<number>>(() => new Set());
  const [search, setSearch] = useState("");
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const [debouncedSearch] = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const [previousSearch, setPreviousSearch] = useState(debouncedSearch);
  if (previousSearch !== debouncedSearch) {
    setPreviousSearch(debouncedSearch);
    if (applyMode === "all") {
      setApplyMode("explicit");
      setExcludedIds(new Set());
    }
  }

  const {
    data,
    isLoading,
    isError,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<BrandingTargetsResponse>({
    queryKey: [
      "properties",
      String(sourceProperty.id),
      "branding_targets",
      debouncedSearch,
      BRANDING_TARGETS_PER_PAGE
    ],
    queryFn: ({ pageParam }) =>
      addressApi.getBrandingTargets({
        propertyId: sourceProperty.id,
        page: Number(pageParam),
        per_page: BRANDING_TARGETS_PER_PAGE,
        search: debouncedSearch.trim() || undefined
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.current_page + 1 : undefined,
    enabled: opened
  });

  const targets = useMemo<BrandingTarget[]>(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data]
  );

  const targetById = useMemo(
    () => new Map(targets.map((target) => [target.id, target])),
    [targets]
  );

  const freshSelected = useMemo(
    () =>
      Array.from(selected.values()).map(
        (target) => targetById.get(target.id) ?? target
      ),
    [selected, targetById]
  );

  const overwriteTargets = useMemo(
    () => freshSelected.filter((target) => target.has_existing_branding),
    [freshSelected]
  );

  const appliedLandlordFields = useMemo(
    () => sourceLandlordFields(sourceProperty.branding),
    [sourceProperty.branding]
  );

  const pmsTargets = useMemo(
    () =>
      freshSelected.filter(
        (target) =>
          overriddenPmsFields(target, appliedLandlordFields).length > 0
      ),
    [freshSelected, appliedLandlordFields]
  );

  const totalMatchingCount = data?.pages[0]?.total_count ?? 0;
  const isAllMode = applyMode === "all";
  const checkedIds = useMemo(() => {
    if (!isAllMode) return selected;

    const map = new Map<number, BrandingTarget>();
    targets.forEach((target) => {
      if (!excludedIds.has(target.id)) map.set(target.id, target);
    });
    return map;
  }, [isAllMode, selected, targets, excludedIds]);

  const selectedCount = isAllMode
    ? Math.max(totalMatchingCount - excludedIds.size, 0)
    : selected.size;
  const isOverCap = !isAllMode && selected.size > MAX_BRANDING_TARGETS;

  const closeAndReset = () => {
    setSelected(new Map());
    setApplyMode("explicit");
    setExcludedIds(new Set());
    setSearch("");
    setIsConfirmOpen(false);
    onClose();
  };

  const { mutate: applyBranding, isPending: isEnqueuing } = useMutation({
    mutationKey: ["BULK", "APPLY", "BRANDING"],
    mutationFn: (
      payload:
        | { target_property_ids: number[] }
        | {
            apply_to_all: true;
            search?: string;
            excluded_property_ids?: number[];
          }
    ) =>
      addressApi.applyBranding({
        propertyId: sourceProperty.id,
        ...payload
      }),
    onSuccess: (response) => {
      startTracking({
        sourcePropertyId: sourceProperty.id,
        bulkApplyId: response.id
      });
      closeAndReset();
    },
    onError: (mutationError) => {
      setIsConfirmOpen(false);
      toast.error(
        `Failed to apply branding: ${getApplyErrorMessage(mutationError, dictionary)}`
      );
    }
  });

  const handleClose = () => {
    if (isEnqueuing) return;

    closeAndReset();
  };

  const handleConfirmClose = () => {
    if (isEnqueuing) return;

    setIsConfirmOpen(false);
  };

  const toggleTarget = (target: BrandingTarget) => {
    if (isAllMode) {
      setExcludedIds((prev) => {
        const next = new Set(prev);
        if (next.has(target.id)) next.delete(target.id);
        else next.add(target.id);

        return next;
      });
      return;
    }

    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(target.id)) next.delete(target.id);
      else next.set(target.id, target);

      return next;
    });
  };

  const handleSelectAllClick = () => {
    setSelected(new Map());
    setExcludedIds(new Set());
    setApplyMode(isAllMode ? "explicit" : "all");
  };

  const runApply = () => {
    if (isAllMode) {
      applyBranding({
        apply_to_all: true,
        search: debouncedSearch.trim() || undefined,
        excluded_property_ids: Array.from(excludedIds)
      });
      return;
    }

    applyBranding({ target_property_ids: Array.from(selected.keys()) });
  };

  const needsConfirm =
    isAllMode || overwriteTargets.length > 0 || pmsTargets.length > 0;

  const handleApplyClick = () => {
    if (needsConfirm) {
      setIsConfirmOpen(true);
      return;
    }

    runApply();
  };

  return (
    <>
      <Modal
        onClose={handleClose}
        opened={opened && !isConfirmOpen}
        panelClassName="flex max-h-[90vh] w-full max-w-[720px] flex-col overflow-hidden"
      >
        <header className="shrink-0 border-b border-alpha-40 p-6">
          <div className="flex items-start justify-between gap-4">
            <h3 className="text-xl font-bold text-content-1000">
              Apply branding to multiple {dictionary.properties}
            </h3>
            <IconButton
              HugeIcon={Cancel01Icon}
              disabled={isEnqueuing}
              onClick={handleClose}
              size={IconButtonSize.LG}
              variant={IconButtonVariant.TEXT_BASE}
            />
          </div>
          <p className="mt-2 text-sm text-content-400">
            Branding saved on {sourceProperty.name} will be applied to the{" "}
            {dictionary.properties} you select below.
          </p>
        </header>

        {sourceProperty.branding ? (
          <div className="shrink-0 border-b border-alpha-40 px-6 py-4">
            <BrandingToApplyPreview
              branding={sourceProperty.branding}
              showLandlordFields={showLandlordFields}
            />
          </div>
        ) : null}

        <div className="flex min-h-0 flex-1 flex-col space-y-2 px-6 py-4">
          <p className="shrink-0 text-xs font-medium text-content-400">
            {dictionary.Properties}
          </p>

          <div className="flex shrink-0 items-center justify-between gap-4">
            <BaseInput
              className="w-full max-w-[293px]"
              id="branding-target-search"
              onChange={setSearch}
              placeholder="Search"
              size={InputSize.SM}
              type="search"
              value={search}
            />
            <Button
              className="shrink-0"
              disabled={totalMatchingCount === 0}
              onClick={handleSelectAllClick}
              variant={ButtonVariant.TEXT}
            >
              {isAllMode ? "Deselect all" : "Select all"}
            </Button>
          </div>

          {isAllMode ? (
            <p className="shrink-0 text-xs text-content-300">
              Applying to every {dictionary.property} matching your search —
              uncheck any you want to skip. This overwrites existing branding on
              affected {dictionary.properties}, and takes their landlord contact
              fields off PMS sync.
            </p>
          ) : null}

          <BrandingTargetsList
            error={error}
            hasNextPage={hasNextPage}
            hasSearch={Boolean(debouncedSearch.trim())}
            isError={isError}
            isFetchingNextPage={isFetchingNextPage}
            isLoading={isLoading}
            onLoadMore={fetchNextPage}
            onToggle={toggleTarget}
            selectedIds={checkedIds}
            visibleTargets={targets}
          />
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-4 border-t border-alpha-40 px-6 py-4">
          <div className="min-w-0 space-y-1">
            <p className="text-sm text-content-400">
              {selectedCount} {propertiesLabel(selectedCount, dictionary)}{" "}
              selected
            </p>
            {isOverCap ? (
              <p className="text-sm font-medium text-danger-500">
                You can apply to at most {MAX_BRANDING_TARGETS}{" "}
                {dictionary.properties} at a time.
              </p>
            ) : null}
            {hasActiveRun ? (
              <p className="text-sm font-medium text-warning-600">
                A previous bulk branding run is still in progress — wait for it
                to finish or cancel it (top-right) before starting another.
              </p>
            ) : null}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <Button disabled={isEnqueuing} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              disabled={selectedCount === 0 || isOverCap || hasActiveRun}
              loading={isEnqueuing}
              onClick={handleApplyClick}
              variant={ButtonVariant.PRIMARY}
            >
              Apply branding
            </Button>
          </div>
        </footer>
      </Modal>

      <ConfirmApplyBrandingDialog
        appliedLandlordFields={appliedLandlordFields}
        canClose={!isEnqueuing}
        isAllMode={isAllMode}
        isPending={isEnqueuing}
        onClose={handleConfirmClose}
        onConfirm={runApply}
        opened={isConfirmOpen}
        overwriteTargets={overwriteTargets}
        pmsTargets={pmsTargets}
        selectedCount={selectedCount}
        sourceName={sourceProperty.name}
      />
    </>
  );
};
